import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Heart, Plus, Save, Loader2, Trash2, Image as ImageIcon, Video, Edit, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, cur = 'eur') => new Intl.NumberFormat(undefined, { style: 'currency', currency: (cur || 'eur').toUpperCase() }).format(Number(n || 0));

const emptySlide = () => ({ type: 'image', image_url: '', video_url: '', title: '', subtitle: '', order: 0, active: true });

const AdminDonations = () => {
    const [settings, setSettings] = useState({ target_amount: 50000, currency: 'eur' });
    const [slides, setSlides] = useState([]);
    const [donations, setDonations] = useState({ results: [], total: 0, total_amount: 0, completed_count: 0, page: 1, size: 50 });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [slideDialog, setSlideDialog] = useState(null); // null | { ...slide } | 'new'
    const [slideForm, setSlideForm] = useState(emptySlide());
    const [page, setPage] = useState(1);

    useEffect(() => { fetchAll(); }, []);
    useEffect(() => { fetchDonations(page); }, [page]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [s, sl, d] = await Promise.all([
                axios.get(`${API_URL}/api/donations/settings`, { withCredentials: true }),
                axios.get(`${API_URL}/api/donations/slides`, { withCredentials: true }),
                axios.get(`${API_URL}/api/donations/list?page=1&size=50`, { withCredentials: true }),
            ]);
            setSettings(s.data);
            setSlides(sl.data || []);
            setDonations(d.data);
        } catch (e) { toast.error('Failed to load donations data'); }
        finally { setLoading(false); }
    };

    const fetchDonations = async (p) => {
        try {
            const { data } = await axios.get(`${API_URL}/api/donations/list?page=${p}&size=50`, { withCredentials: true });
            setDonations(data);
        } catch { /* silent */ }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(`${API_URL}/api/donations/settings`, settings, { withCredentials: true });
            setSettings(data);
            toast.success('Settings saved');
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };

    const openNewSlide = () => { setSlideForm({ ...emptySlide(), order: slides.length }); setSlideDialog('new'); };
    const openEditSlide = (s) => { setSlideForm({ ...s }); setSlideDialog(s); };

    const saveSlide = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            if (slideDialog === 'new') {
                await axios.post(`${API_URL}/api/donations/slides`, slideForm, { withCredentials: true });
                toast.success('Slide added');
            } else {
                await axios.put(`${API_URL}/api/donations/slides/${slideDialog.id}`, slideForm, { withCredentials: true });
                toast.success('Slide updated');
            }
            setSlideDialog(null); fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        finally { setSaving(false); }
    };

    const deleteSlide = async (id) => {
        if (!window.confirm('Delete this slide?')) return;
        try { await axios.delete(`${API_URL}/api/donations/slides/${id}`, { withCredentials: true }); toast.success('Slide deleted'); fetchAll(); }
        catch { toast.error('Failed to delete'); }
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-donations-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><Heart className="h-6 w-6 text-[#DC2626]" />Donations</h1>
                        <p className="text-[#4B5563]">Manage the public donate page and view all donations.</p>
                    </div>
                    <a href="/donate" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#064E3B] hover:underline" data-testid="open-public-donate">
                        <ExternalLink className="h-4 w-4" />Open /donate page
                    </a>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="card-surface"><CardContent className="p-4">
                        <p className="text-xs text-[#6B7280] uppercase tracking-wide">Total Collected</p>
                        <p className="text-3xl font-bold text-[#064E3B] font-['Outfit']" data-testid="kpi-total">{fmt(donations.total_amount, settings.currency)}</p>
                    </CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4">
                        <p className="text-xs text-[#6B7280] uppercase tracking-wide">Completed Donations</p>
                        <p className="text-3xl font-bold text-[#7C3AED] font-['Outfit']">{donations.completed_count}</p>
                    </CardContent></Card>
                    <Card className="card-surface"><CardContent className="p-4">
                        <p className="text-xs text-[#6B7280] uppercase tracking-wide">Target</p>
                        <p className="text-3xl font-bold text-[#B45309] font-['Outfit']">{fmt(settings.target_amount, settings.currency)}</p>
                    </CardContent></Card>
                </div>

                <Tabs defaultValue="donations" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="donations" data-testid="tab-donations">All Donations</TabsTrigger>
                        <TabsTrigger value="slides" data-testid="tab-slides">Slides</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
                    </TabsList>

                    <TabsContent value="donations">
                        <Card className="card-surface"><CardContent className="p-0">
                            {donations.results.length ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Donor</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Country</TableHead>
                                        <TableHead>Amount</TableHead>
                                        <TableHead>Mode</TableHead>
                                        <TableHead>In honour of</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>{donations.results.map(d => (
                                        <TableRow key={d.id} data-testid={`admin-donation-row-${d.id}`}>
                                            <TableCell className="text-xs">{new Date(d.created_at).toLocaleString()}</TableCell>
                                            <TableCell>
                                                {d.anonymous ? <em className="text-[#6B7280]">Anonymous ({d.donor_name || '—'})</em> : d.donor_name}
                                            </TableCell>
                                            <TableCell className="text-xs">{d.donor_email}</TableCell>
                                            <TableCell className="text-xs">{d.country_code || '—'}</TableCell>
                                            <TableCell className="font-semibold text-[#064E3B]">{fmt(d.amount, d.currency)}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="capitalize">{d.interval ? `${d.interval}ly` : 'one-time'}</Badge>
                                                {d.is_recurring_renewal && <span className="ml-2 text-[10px] text-[#7C3AED]">↻ renewal</span>}
                                            </TableCell>
                                            <TableCell className="text-xs">{d.in_honour_of || '—'}</TableCell>
                                            <TableCell><Badge className={d.status === 'completed' ? 'bg-[#16A34A]' : 'bg-[#F59E0B]'}>{d.status}</Badge></TableCell>
                                        </TableRow>
                                    ))}</TableBody>
                                </Table>
                            ) : (
                                <p className="text-center text-[#6B7280] py-10">No donations yet.</p>
                            )}
                            {donations.total > donations.size && (
                                <div className="flex justify-center gap-2 p-4">
                                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                                    <span className="self-center text-xs text-[#6B7280]">Page {donations.page} of {Math.ceil(donations.total / donations.size) || 1}</span>
                                    <Button size="sm" variant="outline" disabled={page * donations.size >= donations.total} onClick={() => setPage(p => p + 1)}>Next</Button>
                                </div>
                            )}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="slides">
                        <Card className="card-surface">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Public /donate slides</CardTitle>
                                <Button size="sm" className="bg-[#064E3B] hover:bg-[#047857]" onClick={openNewSlide} data-testid="add-slide"><Plus className="h-4 w-4 mr-1" />Add slide</Button>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {slides.length === 0 && <p className="text-center text-[#6B7280] py-8">No slides yet. Add images or short videos to promote the campaign.</p>}
                                {slides.sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => (
                                    <div key={s.id} className="flex items-center gap-4 p-3 border border-[#E5E7EB] rounded-lg" data-testid={`slide-row-${s.id}`}>
                                        <div className="w-20 h-14 rounded overflow-hidden bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                                            {s.type === 'video' ? <Video className="h-6 w-6 text-[#4B5563]" /> :
                                                s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> :
                                                <ImageIcon className="h-6 w-6 text-[#4B5563]" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{s.title || <em className="text-[#6B7280]">Untitled</em>}</p>
                                            <p className="text-xs text-[#6B7280] truncate">{s.subtitle || (s.type === 'video' ? s.video_url : s.image_url)}</p>
                                        </div>
                                        <Badge variant="outline">order {s.order || 0}</Badge>
                                        {!s.active && <Badge className="bg-[#9CA3AF]">disabled</Badge>}
                                        <Button size="sm" variant="ghost" onClick={() => openEditSlide(s)}><Edit className="h-4 w-4" /></Button>
                                        <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => deleteSlide(s.id)} data-testid={`delete-slide-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="settings">
                        <Card className="card-surface"><CardContent className="p-6 space-y-5 max-w-md">
                            <div className="space-y-2">
                                <Label>Fundraising target (€)</Label>
                                <Input type="number" min="0" step="100" value={settings.target_amount} onChange={e => setSettings({ ...settings, target_amount: e.target.value })} data-testid="target-amount-input" />
                                <p className="text-xs text-[#6B7280]">Displayed inside the animated progress ring on the public page.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Currency</Label>
                                <Select value={settings.currency} onValueChange={v => setSettings({ ...settings, currency: v })}>
                                    <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="eur">EUR (€)</SelectItem>
                                        <SelectItem value="usd">USD ($)</SelectItem>
                                        <SelectItem value="gbp">GBP (£)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button className="bg-[#064E3B] hover:bg-[#047857]" onClick={saveSettings} disabled={saving} data-testid="save-settings">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save settings
                            </Button>
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>

                {/* Slide dialog */}
                <Dialog open={!!slideDialog} onOpenChange={(o) => !o && setSlideDialog(null)}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle>{slideDialog === 'new' ? 'Add slide' : 'Edit slide'}</DialogTitle></DialogHeader>
                        <form onSubmit={saveSlide} className="space-y-3">
                            <div className="space-y-2">
                                <Label>Type</Label>
                                <Select value={slideForm.type} onValueChange={v => setSlideForm({ ...slideForm, type: v })}>
                                    <SelectTrigger data-testid="slide-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="image">Image</SelectItem>
                                        <SelectItem value="video">Video (mp4 or YouTube)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {slideForm.type === 'image' ? (
                                <div className="space-y-2"><Label>Image URL</Label>
                                    <Input placeholder="https://…jpg" value={slideForm.image_url} onChange={e => setSlideForm({ ...slideForm, image_url: e.target.value })} data-testid="slide-image-url" />
                                </div>
                            ) : (
                                <div className="space-y-2"><Label>Video URL (mp4 or YouTube watch/embed link)</Label>
                                    <Input placeholder="https://youtube.com/watch?v=… or https://…mp4" value={slideForm.video_url} onChange={e => setSlideForm({ ...slideForm, video_url: e.target.value })} data-testid="slide-video-url" />
                                </div>
                            )}
                            <div className="space-y-2"><Label>Title</Label>
                                <Input value={slideForm.title} onChange={e => setSlideForm({ ...slideForm, title: e.target.value })} data-testid="slide-title" />
                            </div>
                            <div className="space-y-2"><Label>Subtitle</Label>
                                <Input value={slideForm.subtitle} onChange={e => setSlideForm({ ...slideForm, subtitle: e.target.value })} data-testid="slide-subtitle" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2"><Label>Order</Label>
                                    <Input type="number" value={slideForm.order} onChange={e => setSlideForm({ ...slideForm, order: e.target.value })} data-testid="slide-order" />
                                </div>
                                <div className="space-y-2"><Label>Status</Label>
                                    <Select value={slideForm.active ? 'active' : 'inactive'} onValueChange={v => setSlideForm({ ...slideForm, active: v === 'active' })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Disabled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setSlideDialog(null)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-slide">
                                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default AdminDonations;
